import { until } from "@vueuse/core";

import { computed, watch } from "vue";

import { useWorktreeManager } from "@/composables/worktree-manager";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { type ProcessInTree } from "@/stores/processes-store";

import { ProcessEntity } from ".";
import { BaseLinkedEntitiesManager } from "../mystekz-linked-entities/manager";

type GroupedProcess = ProcessInTree & { group: string };

/**
 * Manages the business entities and replaces
 */
export class ProcessesManager extends BaseLinkedEntitiesManager<ProcessEntity> {
    nodeType = "process" as const;
    worktreeManager = useWorktreeManager();
    currentEntityManager = useCurrentEntityManager();

    constructor(baseUrl: string) {
        super(baseUrl);
        const isLoading = computed(() => {
            return (
                this.worktreeManager.isFetching.value ||
                this.organizationManager.isLoadingOrganizations.value ||
                this.stekManager.isLoadingSteks.value
            );
        });
        this.ready = until(isLoading).not.toBeTruthy();

        watch(
            [
                this.worktreeManager.worktree,
                () => this.currentEntityManager.domain.value?.identity,
                () => this.currentEntityManager.product.value?.identity,
            ],
            async ([newWorktree, currentDomainId, currentProductId]) => {
                if (!newWorktree || !currentDomainId || !currentProductId) {
                    this.setEntities([]);
                    return;
                }

                const processes: GroupedProcess[] = [];
                for (const domain of newWorktree.domains) {
                    if (domain.identity !== currentDomainId) {
                        continue;
                    }

                    for (const product of domain.products) {
                        if (product.identity !== currentProductId) {
                            continue;
                        }
                        for (const process of product.processes) {
                            processes.push({
                                ...process,
                                group: product.name,
                            });
                        }
                    }
                }

                this.setEntities(processes);
            },
            { immediate: true }
        );
    }
}
